'use client';

import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useId, useRef, useState } from 'react';

import {
	ConsoleLoginError,
	isPasskeyConditionalMediationAvailable,
	loginToConsole,
	loginToConsoleWithPasskey,
} from '@/lib/auth/console-login';
import { LOGIN_URL } from '@/lib/constants';

import { SceneCanvas } from './SceneCanvas';

type Mode = 'login' | 'register';
type Method = 'password' | 'key';
type Stage = 'form' | '2fa';
type StatusKey =
	| 'statusAwaitingCredentials'
	| 'statusAuthenticating'
	| 'statusAwaiting2fa'
	| 'statusVerifyingCode'
	| 'statusAccessGranted'
	| 'statusLoginFailed'
	| 'statusEnterCode';
type ErrorKey =
	| 'errorInvalidCredentials'
	| 'errorNetwork'
	| 'errorGeneric'
	| 'errorUnsupportedOnLanding';

const OTP_LEN = 6;
const PRODUCTION_LANDING_HOSTS = new Set(['onekyc.io', 'www.onekyc.io']);
const INVALID_LOGIN_CODES = new Set([
	'invalid_credentials',
	'invalid_credential',
	'invalid_password',
	'invalid_totp',
	'invalid_code',
	'totp_invalid',
	'totp_failed',
	'unauthorized',
]);

function getOtpValue(values: string[], index: number): string {
	switch (index) {
		case 0:
			return values[0] ?? '';
		case 1:
			return values[1] ?? '';
		case 2:
			return values[2] ?? '';
		case 3:
			return values[3] ?? '';
		case 4:
			return values[4] ?? '';
		case 5:
			return values[5] ?? '';
		default:
			return '';
	}
}

function setOtpValue(values: string[], index: number, value: string): string[] {
	const next = values.slice();
	switch (index) {
		case 0:
			next[0] = value;
			break;
		case 1:
			next[1] = value;
			break;
		case 2:
			next[2] = value;
			break;
		case 3:
			next[3] = value;
			break;
		case 4:
			next[4] = value;
			break;
		case 5:
			next[5] = value;
			break;
		default:
			break;
	}
	return next;
}

function focusOtpRef(
	refs: React.RefObject<Array<HTMLInputElement | null>>,
	index: number,
) {
	switch (index) {
		case 0:
			refs.current[0]?.focus();
			break;
		case 1:
			refs.current[1]?.focus();
			break;
		case 2:
			refs.current[2]?.focus();
			break;
		case 3:
			refs.current[3]?.focus();
			break;
		case 4:
			refs.current[4]?.focus();
			break;
		case 5:
			refs.current[5]?.focus();
			break;
		default:
			break;
	}
}

function setOtpRef(
	refs: React.RefObject<Array<HTMLInputElement | null>>,
	index: number,
	element: HTMLInputElement | null,
) {
	switch (index) {
		case 0:
			refs.current[0] = element;
			break;
		case 1:
			refs.current[1] = element;
			break;
		case 2:
			refs.current[2] = element;
			break;
		case 3:
			refs.current[3] = element;
			break;
		case 4:
			refs.current[4] = element;
			break;
		case 5:
			refs.current[5] = element;
			break;
		default:
			break;
	}
}

function canStartConditionalPasskey(): boolean {
	return (
		process.env.NEXT_PUBLIC_CONSOLE_WEBAUTHN_LOGIN_OPTIONS_API_URL != null ||
		PRODUCTION_LANDING_HOSTS.has(window.location.hostname)
	);
}

/**
 * Full login panel — a faithful React port of the visual prototype (tabs,
 * password reveal, security-key / passkey, 2FA OTP, live terminal status line).
 *
 * First-party console login panel. Password, TOTP, and passkey submits call the
 * console auth helpers; unsupported visual affordances never grant access locally.
 */
export function AuthPanel({
	initialMode: _initialMode = 'login',
	onClose,
	onRedirect = (url) => window.location.assign(url),
}: {
	initialMode?: Mode;
	/** When provided, the panel renders in modal mode (no full-page wrapper)
	 *  and shows a close affordance. */
	onClose?: () => void;
	onRedirect?: (url: string) => void;
}) {
	const t = useTranslations('Auth');
	const isModal = onClose != null;

	void _initialMode;
	const [method, setMethod] = useState<Method>('password');
	const [stage, setStage] = useState<Stage>('form');
	const [show, setShow] = useState(false);
	const [email, setEmail] = useState('');
	const [pass, setPass] = useState('');
	const [code, setCode] = useState<string[]>(() =>
		Array<string>(OTP_LEN).fill(''),
	);
	const [statusKey, setStatusKey] = useState<StatusKey>(
		'statusAwaitingCredentials',
	);
	const [errorKey, setErrorKey] = useState<ErrorKey | null>(null);
	const [submitting, setSubmitting] = useState(false);
	const [granted, setGranted] = useState(false);

	const t1 = useRef<ReturnType<typeof setTimeout> | null>(null);
	const otpRefs = useRef<Array<HTMLInputElement | null>>([]);
	const sidRef = useRef<HTMLSpanElement>(null);
	const conditionalPasskeyAbortRef = useRef<AbortController | null>(null);
	const requestSeqRef = useRef(0);
	const modeRef = useRef<Mode>('login');
	const methodRef = useRef<Method>('password');
	const stageRef = useRef<Stage>('form');
	const emailId = useId();
	const passId = useId();

	// Client-only session id for the HUD — written straight to the DOM (same
	// ref pattern as <Clock />) so SSR stays deterministic and no setState runs
	// inside the effect.
	useEffect(() => {
		if (sidRef.current) {
			sidRef.current.textContent = Math.random()
				.toString(16)
				.slice(2, 10)
				.toUpperCase();
		}
	}, []);

	const clearTimers = () => {
		if (t1.current) clearTimeout(t1.current);
		t1.current = null;
	};
	useEffect(() => clearTimers, []);

	const isLogin = true;
	const is2fa = stage === '2fa';
	const showForm = stage === 'form';
	const showPass = method === 'password';
	const showKey = isLogin && method === 'key';
	const showForgot = isLogin && method === 'password';
	const showKeyOption = isLogin && method === 'password';
	const showBackToPass = isLogin && method === 'key';
	const codeComplete = code.every((c) => c !== '');

	const nextRequestSeq = () => {
		requestSeqRef.current += 1;
		return requestSeqRef.current;
	};

	const invalidateRequests = () => {
		requestSeqRef.current += 1;
	};

	const abortConditionalPasskey = () => {
		conditionalPasskeyAbortRef.current?.abort();
		conditionalPasskeyAbortRef.current = null;
	};

	const setMethodState = (next: Method) => {
		methodRef.current = next;
		setMethod(next);
	};

	const setStageState = (next: Stage) => {
		stageRef.current = next;
		setStage(next);
	};

	const isCurrentRequest = (
		requestId: number,
		expected: { mode: Mode; method: Method; stage: Stage },
	) =>
		requestSeqRef.current === requestId &&
		modeRef.current === expected.mode &&
		methodRef.current === expected.method &&
		stageRef.current === expected.stage;

	useEffect(() => invalidateRequests, []);

	const closePanel = () => {
		invalidateRequests();
		abortConditionalPasskey();
		clearTimers();
		setSubmitting(false);
		onClose?.();
	};

	const toMethod = (next: Method) => {
		invalidateRequests();
		abortConditionalPasskey();
		clearTimers();
		setMethodState(next);
		setStageState('form');
		setStatusKey('statusAwaitingCredentials');
		setErrorKey(null);
		setSubmitting(false);
	};

	const mapLoginError = (error: unknown): ErrorKey => {
		if (!(error instanceof ConsoleLoginError)) {
			return 'errorGeneric';
		}
		if (error.code === 'network_error') {
			return 'errorNetwork';
		}
		if (INVALID_LOGIN_CODES.has(error.code)) {
			return 'errorInvalidCredentials';
		}
		return 'errorGeneric';
	};

	const completeLogin = useCallback(
		(redirectTo: string) => {
			setStatusKey('statusAccessGranted');
			setSubmitting(false);
			setGranted(true);
			onRedirect(redirectTo);
		},
		[onRedirect],
	);

	const focusFirstOtp = () => {
		clearTimers();
		t1.current = setTimeout(() => focusOtpRef(otpRefs, 0), 0);
	};

	const submit = async () => {
		if (submitting) return;
		const requestId = nextRequestSeq();
		setSubmitting(true);
		setErrorKey(null);
		setStatusKey('statusAuthenticating');
		clearTimers();
		try {
			const result = await loginToConsole({ email, password: pass });
			if (
				!isCurrentRequest(requestId, {
					mode: 'login',
					method: 'password',
					stage: 'form',
				})
			) {
				return;
			}
			completeLogin(result.redirectTo);
		} catch (error) {
			if (
				!isCurrentRequest(requestId, {
					mode: 'login',
					method: 'password',
					stage: 'form',
				})
			) {
				return;
			}
			if (
				error instanceof ConsoleLoginError &&
				error.code === 'totp_required'
			) {
				setStageState('2fa');
				setStatusKey('statusAwaiting2fa');
				setCode(Array<string>(OTP_LEN).fill(''));
				setSubmitting(false);
				focusFirstOtp();
				return;
			}
			setErrorKey(mapLoginError(error));
			setStatusKey('statusLoginFailed');
			setSubmitting(false);
		}
	};

	const keyAuth = async () => {
		if (submitting) return;
		abortConditionalPasskey();
		const requestId = nextRequestSeq();
		clearTimers();
		setSubmitting(true);
		setGranted(false);
		setErrorKey(null);
		setStatusKey('statusAuthenticating');
		try {
			const result = await loginToConsoleWithPasskey();
			if (
				!isCurrentRequest(requestId, {
					mode: 'login',
					method: 'key',
					stage: 'form',
				})
			) {
				return;
			}
			completeLogin(result.redirectTo);
		} catch (error) {
			if (
				!isCurrentRequest(requestId, {
					mode: 'login',
					method: 'key',
					stage: 'form',
				})
			) {
				return;
			}
			setErrorKey(mapLoginError(error));
			setStatusKey('statusLoginFailed');
			setSubmitting(false);
		}
	};

	const confirm2fa = async () => {
		if (submitting) return;
		if (!codeComplete) {
			setStatusKey('statusEnterCode');
			return;
		}
		const requestId = nextRequestSeq();
		setSubmitting(true);
		setErrorKey(null);
		setStatusKey('statusVerifyingCode');
		clearTimers();
		try {
			const result = await loginToConsole({
				email,
				password: pass,
				totp: code.join(''),
			});
			if (
				!isCurrentRequest(requestId, {
					mode: 'login',
					method: 'password',
					stage: '2fa',
				})
			) {
				return;
			}
			completeLogin(result.redirectTo);
		} catch (error) {
			if (
				!isCurrentRequest(requestId, {
					mode: 'login',
					method: 'password',
					stage: '2fa',
				})
			) {
				return;
			}
			setErrorKey(mapLoginError(error));
			setStatusKey('statusLoginFailed');
			setSubmitting(false);
		}
	};

	const onPrimary = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		if (isLogin && method === 'key') void keyAuth();
		else void submit();
	};

	useEffect(() => {
		if (
			!isLogin ||
			method !== 'password' ||
			stage !== 'form' ||
			submitting ||
			!canStartConditionalPasskey()
		) {
			return undefined;
		}
		const requestId = nextRequestSeq();
		const controller = new AbortController();
		conditionalPasskeyAbortRef.current = controller;
		void (async () => {
			if (!(await isPasskeyConditionalMediationAvailable())) {
				return;
			}
			const result = await loginToConsoleWithPasskey({
				mediation: 'conditional',
				signal: controller.signal,
			});
			if (
				!isCurrentRequest(requestId, {
					mode: 'login',
					method: 'password',
					stage: 'form',
				})
			) {
				return;
			}
			completeLogin(result.redirectTo);
		})().catch(() => {});
		return () => {
			controller.abort();
			if (conditionalPasskeyAbortRef.current === controller) {
				conditionalPasskeyAbortRef.current = null;
			}
		};
	}, [completeLogin, isLogin, method, stage, submitting]);

	const onOtp = (i: number, raw: string) => {
		const digit = raw.replace(/\D/g, '').slice(-1);
		setCode((prev) => setOtpValue(prev, i, digit));
		if (digit && i < OTP_LEN - 1) focusOtpRef(otpRefs, i + 1);
	};

	const onOtpKeyDown = (
		i: number,
		e: React.KeyboardEvent<HTMLInputElement>,
	) => {
		if (e.key === 'Backspace' && getOtpValue(code, i) === '' && i > 0)
			focusOtpRef(otpRefs, i - 1);
	};

	const modeCode = is2fa
		? 'LOGIN · 2FA'
		: method === 'key'
			? 'LOGIN · KEY'
			: 'LOGIN';

	const title = is2fa ? t('title2fa') : t('titleLogin');
	const primaryText = method === 'key' ? t('primaryKey') : t('primaryLogin');

	const sceneId = 'auth-login';

	const tabClass = (active: boolean) =>
		`flex-1 cursor-crosshair border px-2.5 py-2.5 text-center font-mono text-[11px] uppercase tracking-[0.15em] transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-fg ${
			active
				? 'border-fg bg-fg text-bg'
				: 'border-border text-muted hover:border-fg'
		}`;

	const fieldClass =
		'mt-3 block min-h-11 w-full border-0 border-b border-border bg-transparent py-2.5 font-mono text-[15px] text-fg outline-none focus-visible:border-fg';

	const primaryBtnClass =
		'mt-6 flex min-h-11 w-full cursor-crosshair items-center justify-between border border-fg bg-fg px-[18px] py-4 font-mono text-xs uppercase tracking-[0.15em] text-bg transition-opacity hover:opacity-[0.88] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-fg focus-visible:ring-offset-2 focus-visible:ring-offset-bg disabled:cursor-not-allowed disabled:opacity-60';

	return (
		<section
			className={
				isModal
					? 'text-fg flex w-full flex-col items-center'
					: 'bg-surface text-fg flex min-h-screen flex-col items-center justify-center px-4 py-24 sm:px-8 md:py-28'
			}
		>
			{/* Chrome header */}
			<div className="text-muted mb-3.5 flex w-full max-w-[980px] justify-between gap-3 font-mono text-[11px] tracking-[0.09em] uppercase">
				<span>SECTION_00 // AUTH</span>
				<span>SECURE CHANNEL</span>
			</div>

			<div className="border-fg bg-bg relative flex w-full max-w-[980px] flex-wrap border">
				{isModal && (
					<button
						type="button"
						onClick={closePanel}
						aria-label={t('closeLabel')}
						className="text-muted hover:text-fg focus-visible:ring-fg absolute top-3 right-3 z-10 flex h-8 w-8 cursor-crosshair items-center justify-center font-mono text-lg leading-none transition-colors focus-visible:ring-1 focus-visible:outline-none"
					>
						×
					</button>
				)}
				{/* Left — animated identity scene */}
				<div className="relative min-h-[360px] flex-[1_1_340px] overflow-hidden bg-[#0a0a0b]">
					<SceneCanvas
						scene={sceneId}
						className="absolute inset-0 h-full w-full"
					/>
					<div className="auth-scanline pointer-events-none absolute inset-x-0 top-[-12%] h-[30%] bg-[linear-gradient(rgba(255,255,255,0),rgba(255,255,255,0.08),rgba(255,255,255,0))] mix-blend-screen" />
					<div className="pointer-events-none absolute inset-0 shadow-[inset_0_0_70px_rgba(0,0,0,0.5)]" />
					<div className="pointer-events-none absolute inset-x-[18px] top-4 flex justify-between font-mono text-[11px] tracking-[0.15em] text-white/85 [text-shadow:0_1px_3px_rgba(0,0,0,0.6)]">
						<span>ONEKYC</span>
						<span>SYS.ID</span>
					</div>
					<div className="pointer-events-none absolute bottom-4 left-[18px] font-mono text-[10px] leading-[1.7] tracking-[0.08em] text-white/70 [text-shadow:0_1px_3px_rgba(0,0,0,0.6)]">
						AES-256-ENCRYPTED
						<br />
						SESSION:{' '}
						<span ref={sidRef} suppressHydrationWarning>
							········
						</span>
					</div>
					<div className="pointer-events-none absolute right-[18px] bottom-4 bg-white px-2 py-1 font-mono text-[10px] tracking-[0.08em] text-[#0b0b0c]">
						IDENTITY
					</div>
				</div>

				{/* Right — form */}
				<div className="flex min-w-0 flex-[1_1_380px] flex-col p-6 sm:p-8 md:p-10">
					<div className="text-muted font-mono text-[11px] tracking-[0.18em]">
						SYSTEM ACCESS // {modeCode}
					</div>
					<h1 className="mt-2.5 text-[clamp(26px,3.4vw,34px)] leading-[1.05] font-normal tracking-[-0.02em]">
						{title}
					</h1>

					{showForm && (
						<>
							<div className="mt-6 flex">
								<button
									type="button"
									aria-pressed={isLogin}
									className={tabClass(isLogin)}
								>
									{t('tabLogin')}
								</button>
							</div>

							<form onSubmit={onPrimary} noValidate>
								<div className="mt-6">
									<label htmlFor={emailId} className="label-mono">
										{isLogin ? t('emailLabelLogin') : t('emailLabelRegister')}
									</label>
									<input
										id={emailId}
										type="email"
										name="email"
										inputMode="email"
										autoComplete={isLogin ? 'username webauthn' : 'email'}
										placeholder={t('emailPlaceholder')}
										value={email}
										onChange={(e) => setEmail(e.target.value)}
										className={fieldClass}
									/>
								</div>

								{showPass && (
									<div className="mt-5">
										<div className="flex items-baseline justify-between gap-2.5">
											<label htmlFor={passId} className="label-mono">
												{t('passwordLabel')}
											</label>
											<div className="flex items-center gap-3">
												{showForgot && (
													<a
														href={LOGIN_URL}
														rel="noopener noreferrer"
														className="text-muted hover:text-fg font-mono text-[10px] tracking-[0.05em] no-underline"
													>
														{t('forgot')}
													</a>
												)}
												<button
													type="button"
													onClick={() => setShow((v) => !v)}
													aria-pressed={show}
													disabled={submitting}
													className="text-muted hover:text-fg cursor-crosshair font-mono text-[10px] tracking-[0.1em] whitespace-nowrap disabled:cursor-not-allowed disabled:opacity-60"
												>
													{show ? t('hide') : t('show')}
												</button>
											</div>
										</div>
										<div className="border-border mt-3 flex items-center border-b">
											<input
												id={passId}
												type={show ? 'text' : 'password'}
												name="password"
												autoComplete={
													isLogin ? 'current-password' : 'new-password'
												}
												placeholder="••••••••"
												value={pass}
												onChange={(e) => setPass(e.target.value)}
												className="text-fg min-h-11 min-w-0 flex-1 border-0 bg-transparent py-2.5 pr-12 font-mono text-[15px] outline-none"
											/>
										</div>
									</div>
								)}

								{showKey && (
									<div className="border-border-hover mt-5 border border-dashed px-[18px] py-5 text-center">
										<div className="text-muted font-mono text-[10px] tracking-[0.15em]">
											SECURITY KEY // FIDO2 · PASSKEY
										</div>
										<div className="relative mx-auto my-4 h-[62px] w-[62px]">
											<div className="border-fg absolute inset-0 rounded-full border" />
											<div className="auth-keypulse border-fg absolute inset-0 rounded-full border" />
											<div className="text-fg absolute inset-0 flex items-center justify-center font-mono text-[10px] tracking-[0.1em]">
												KEY
											</div>
										</div>
										<p className="text-muted mx-auto max-w-[34ch] text-[13px] leading-[1.5]">
											{t('securityKeyHint')}
										</p>
									</div>
								)}

								<button
									type="submit"
									disabled={submitting}
									className={primaryBtnClass}
								>
									<span>{primaryText}</span>
									<span aria-hidden="true">-&gt;</span>
								</button>
							</form>

							{showKeyOption && (
								<>
									<div className="text-muted mt-[18px] flex items-center gap-3 font-mono text-[10px] tracking-[0.15em]">
										<span className="bg-border h-px flex-1" />
										{t('orDivider')}
										<span className="bg-border h-px flex-1" />
									</div>
									<button
										type="button"
										onClick={() => toMethod('key')}
										disabled={submitting}
										className="border-fg text-fg hover:bg-fg hover:text-bg focus-visible:ring-fg mt-3 flex min-h-11 w-full cursor-crosshair items-center justify-between border bg-transparent px-[18px] py-[15px] font-mono text-xs tracking-[0.15em] uppercase transition-colors focus-visible:ring-1 focus-visible:outline-none"
									>
										<span>{t('loginWithKey')}</span>
										<span aria-hidden="true">-&gt;</span>
									</button>
								</>
							)}

							{showBackToPass && (
								<button
									type="button"
									onClick={() => toMethod('password')}
									disabled={submitting}
									className="text-muted hover:text-fg mt-3.5 cursor-crosshair font-mono text-[11px] tracking-[0.05em]"
								>
									&lt;- {t('loginWithPassword')}
								</button>
							)}
						</>
					)}

					{is2fa && (
						<>
							<p className="text-muted mt-6 font-mono text-[10.5px] leading-[1.5] tracking-[0.08em]">
								{t('twofaHint')}
							</p>
							<div className="mt-4 flex gap-2">
								{code.map((value, i) => (
									<input
										key={i}
										ref={(el) => {
											setOtpRef(otpRefs, i, el);
										}}
										value={value}
										onChange={(e) => onOtp(i, e.target.value)}
										onKeyDown={(e) => onOtpKeyDown(i, e)}
										inputMode="numeric"
										autoComplete={i === 0 ? 'one-time-code' : 'off'}
										aria-label={t('otpCellLabel', { n: i + 1 })}
										placeholder="•"
										className="border-border focus-visible:border-fg text-fg h-[54px] min-w-0 flex-1 border bg-transparent text-center font-mono text-xl outline-none"
									/>
								))}
							</div>
							<button
								type="button"
								onClick={() => void confirm2fa()}
								disabled={submitting}
								className={primaryBtnClass}
							>
								<span>{t('confirm')}</span>
								<span aria-hidden="true">-&gt;</span>
							</button>
							<div className="mt-3.5 flex items-center justify-between gap-3">
								<button
									type="button"
									onClick={() => setStatusKey('statusAwaiting2fa')}
									disabled={submitting}
									className="text-muted hover:text-fg cursor-crosshair font-mono text-[10.5px] tracking-[0.05em]"
								>
									{t('resend')}
								</button>
								<button
									type="button"
									onClick={() => {
										invalidateRequests();
										clearTimers();
										setStageState('form');
										setStatusKey('statusAwaitingCredentials');
										setErrorKey(null);
										setSubmitting(false);
									}}
									className="text-muted hover:text-fg cursor-crosshair font-mono text-[10.5px] tracking-[0.05em]"
								>
									&lt;- {t('back')}
								</button>
							</div>
						</>
					)}

					<p
						aria-live="polite"
						className="text-muted mt-3.5 font-mono text-[10.5px] tracking-[0.08em]"
					>
						&gt; {t(statusKey)}{' '}
						<span className="auth-cursor inline-block">_</span>
					</p>

					{errorKey && (
						<p role="alert" className="text-fg mt-3 font-mono text-xs">
							{t(errorKey)}
						</p>
					)}

					{granted && (
						<a
							href={LOGIN_URL}
							rel="noopener noreferrer"
							className="border-fg text-fg hover:bg-fg hover:text-bg focus-visible:ring-fg mt-4 flex min-h-11 w-full cursor-crosshair items-center justify-between border px-[18px] py-[15px] font-mono text-xs tracking-[0.15em] uppercase no-underline transition-colors focus-visible:ring-1 focus-visible:outline-none"
						>
							<span>{t('continueToConsole')}</span>
							<span aria-hidden="true">-&gt;</span>
						</a>
					)}
				</div>

				{/* Corner brackets */}
				<span className="border-fg pointer-events-none absolute -top-px -left-px h-3.5 w-3.5 border-t-2 border-l-2" />
				<span className="border-fg pointer-events-none absolute -top-px -right-px h-3.5 w-3.5 border-t-2 border-r-2" />
				<span className="border-fg pointer-events-none absolute -bottom-px -left-px h-3.5 w-3.5 border-b-2 border-l-2" />
				<span className="border-fg pointer-events-none absolute -right-px -bottom-px h-3.5 w-3.5 border-r-2 border-b-2" />
			</div>
		</section>
	);
}
